const STORAGE_KEY = "gst-lcp-banner";
const PRELOAD_ID = "gst-lcp-banner-preload";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const ALLOWED_HOST = "media.gitaseva.org";

type LcpBannerHint = {
  url: string;
  lang: string;
  savedAt: number;
};

const isAllowedBannerUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname === ALLOWED_HOST;
  } catch {
    return false;
  }
};

const readHint = (): LcpBannerHint | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LcpBannerHint;
    if (!parsed?.url || !parsed?.lang || !parsed?.savedAt) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const clearLcpBannerHint = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore quota / private-mode failures
  }
};

export const saveLcpBannerHint = (url: string, lang: string): void => {
  if (!url || !lang || !isAllowedBannerUrl(url)) return;
  const hint: LcpBannerHint = { url, lang, savedAt: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(hint));
  } catch {
    // ignore quota / private-mode failures
  }
};

export const getValidLcpBannerHint = (lang: string): string | null => {
  const hint = readHint();
  if (!hint) return null;
  if (hint.lang !== lang) return null;
  if (Date.now() - hint.savedAt > MAX_AGE_MS) {
    clearLcpBannerHint();
    return null;
  }
  if (!isAllowedBannerUrl(hint.url)) {
    clearLcpBannerHint();
    return null;
  }
  return hint.url;
};

export const removeLcpBannerPreload = (): void => {
  document.getElementById(PRELOAD_ID)?.remove();
};

/** Starts a download only. Never used as the visible carousel src. */
export const preloadLcpBannerHint = (lang: string): void => {
  removeLcpBannerPreload();
  const url = getValidLcpBannerHint(lang);
  if (!url) return;

  const link = document.createElement("link");
  link.id = PRELOAD_ID;
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.setAttribute("fetchpriority", "high");
  link.addEventListener("error", () => {
    clearLcpBannerHint();
    removeLcpBannerPreload();
  });
  document.head.appendChild(link);
};
